import React from "react";
import { useGameStateContext } from "../GameStateProvider";
import { useModalActions } from "../hooks/useModalActions";
import { useSeedActions } from "../hooks/useSeedActions";
import { getAllItemsMap } from "../utils/dataUtils";
import { CONSTANTS } from "../data";

// Import modal components
import ShopModal from "./modals/ShopModal";
import ChallengesModal from "./modals/ChallengesModal";
import RecipeBookModal from "./modals/RecipeBookModal";
import SettingsModal from "./modals/SettingsModal";
import ResetConfirmModal from "./modals/ResetConfirmModal";

// Import other UI components
import TutorialOverlay from "./TutorialOverlay";
import LatteArtGame from "./LatteArtGame";
import LevelUpNotification from "./LevelUpNotification";
import TrendTipBanner from "./TrendTipBanner";
import DiscoveryNotification from "./DiscoveryNotification";

interface GlobalUIProps {
  onStartChallenge: (challengeId: string) => void;
}

const GlobalUI: React.FC<GlobalUIProps> = ({ onStartChallenge }) => {
  const { state, dispatch } = useGameStateContext();
  const { handleHideModal } = useModalActions();
  const { handleStartSeeded, handleCopyShareCode } = useSeedActions();

  // Use centralized map creation
  const allItemsMap = getAllItemsMap();

  const handleLatteArtComplete = (accuracy: number) => {
    dispatch({ type: "END_LATTE_ART" });
  };

  const handleLatteArtExit = () => {
    dispatch({ type: "END_LATTE_ART" });
  };

  const handleStartTutorial = (tutorialId: string) => {
    dispatch({ type: "START_TUTORIAL", tutorialId });
  };

  const handlePrestige = () => {
    // Implement prestige logic
  };

  const handleResetDailies = () => {
    // Implement reset dailies logic
  };

  const handleResetGame = () => {
    dispatch({ type: "RESET_GAME" });
    dispatch({ type: "HIDE_MODAL" });
  };

  return (
    <>
      {/* Modals */}
      <ShopModal
        show={state.showModal === "shop"}
        onClose={handleHideModal}
      />
      
      <ChallengesModal
        show={state.showModal === "challenges"}
        onClose={handleHideModal}
        masterChefChallenge={state.masterChefChallenge}
        startChallenge={onStartChallenge}
      />
      
      <RecipeBookModal
        show={state.showModal === "recipeBook"}
        onClose={handleHideModal}
        discoveredRecipes={state.discoveredRecipes}
        lastDiscovery={state.lastDiscovery}
        aiGeneratedRecipes={state.aiGeneratedRecipes}
        nitroUnlocked={state.nitroUnlocked}
        allItemsMap={allItemsMap}
        inventory={state.inventory}
        activeSeason={state.activeSeason}
      />
      
      <SettingsModal
        show={state.showModal === "settings"}
        onClose={handleHideModal}
        musicVolume={state.settings.musicVolume}
        sfxVolume={state.settings.sfxVolume}
        customSeed={state.customSeed}
        onMusicVolumeChange={(v) => dispatch({ type: "UPDATE_SETTINGS", settings: { musicVolume: v } })}
        onSfxVolumeChange={(v) => dispatch({ type: "UPDATE_SETTINGS", settings: { sfxVolume: v } })}
        onCustomSeedChange={(seed) => dispatch({ type: "SET_CUSTOM_SEED", seed })}
        onApplySeed={() => handleStartSeeded(state.customSeed)}
        onResetDailies={handleResetDailies}
        onShowResetConfirm={() => dispatch({ type: "SHOW_MODAL", modal: "resetConfirm" })}
        onPrestige={handlePrestige}
        canPrestige={false}
        prestigeLevel={state.prestigeLevel}
        tutorialState={state.tutorialState}
        startTutorial={handleStartTutorial}
        onCopyShareCode={handleCopyShareCode}
      />
      
      <ResetConfirmModal
        show={state.showModal === "resetConfirm"}
        onCancel={handleHideModal}
        onConfirm={handleResetGame}
      />
      
      {/* Overlays */}
      <TutorialOverlay
        tutorialState={state.tutorialState}
        onUpdateState={(newState) => dispatch({ type: "UPDATE_TUTORIAL_STATE", newState })}
      />
      
      <LatteArtGame
        active={state.latteArtGame.active}
        onComplete={handleLatteArtComplete}
        onExit={handleLatteArtExit}
      />
      
      <LevelUpNotification
        level={state.levelUpNotification}
        onComplete={() => dispatch({ type: "CLEAR_LEVEL_UP" })}
      />
      
      <DiscoveryNotification
        discovery={state.discoveryNotification}
        onComplete={() => dispatch({ type: "CLEAR_DISCOVERY_NOTIFICATION" })}
      />
      
      <TrendTipBanner
        tip={state.trendTip}
        onDismiss={() => dispatch({ type: "HIDE_TREND_TIP" })}
      />
    </>
  );
};

export default GlobalUI;
